import { useEffect, useId, useState, type FormEvent } from "react";
import { useProjects, useUpdateAlertRule } from "../../api/queries";
import type { AlertRule } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import { CONDITION_META } from "./conditionMeta";
import "../../components/Form.css";

interface EditAlertRuleModalProps {
  /** The rule being edited, or null when the dialog is closed — driving
   * this off the target rather than a separate boolean keeps "which rule"
   * and "is it open" from ever disagreeing (mirrors EditUserModal). */
  rule: AlertRule | null;
  onClose: () => void;
}

function looksLikeHttpsUrl(value: string): boolean {
  return value.trim() === "" || value.trim().toLowerCase().startsWith("https://");
}

/**
 * `condition` is immutable (see AlertRule's doc comment in types.ts) — shown
 * read-only here, never an editable field. The signing secret is the one
 * field with real write-only semantics to get right: leaving the password
 * field blank sends `signingSecret: undefined` (server-side: "leave
 * unchanged"); the "Remove signing secret" checkbox is the ONLY way to send
 * an explicit `""` ("clear it") — there is no way to ask "is one currently
 * set?" first, since the API never reads it back (S-13/S-14).
 */
export function EditAlertRuleModal({ rule, onClose }: EditAlertRuleModalProps) {
  const nameId = useId();
  const thresholdId = useId();
  const scopeId = useId();
  const webhookId = useId();
  const secretId = useId();
  const clearSecretId = useId();
  const enabledId = useId();
  const { pushToast } = useToast();
  const mutation = useUpdateAlertRule();
  const { data: projectsPage } = useProjects({ limit: 100 });

  const [name, setName] = useState("");
  const [threshold, setThreshold] = useState(0);
  const [scope, setScope] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [signingSecret, setSigningSecret] = useState("");
  const [clearSecret, setClearSecret] = useState(false);
  const [enabled, setEnabled] = useState(true);

  // Re-seed the form fields whenever a different rule is opened for
  // editing. The signing secret fields never seed from `rule` — there is
  // nothing to seed them WITH (the API never returns the value, S-13).
  useEffect(() => {
    if (!rule) return;
    setName(rule.name);
    setThreshold(rule.threshold);
    setScope(rule.scope ?? "");
    setWebhookUrl(rule.webhookUrl);
    setSigningSecret("");
    setClearSecret(false);
    setEnabled(rule.enabled);
  }, [rule]);

  const meta = rule ? CONDITION_META[rule.condition] : null;
  const projects = projectsPage?.items ?? [];

  function handleClose() {
    setSigningSecret("");
    setClearSecret(false);
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!rule) return;
    const { id, name: ruleName } = rule;
    mutation.mutate(
      {
        id,
        input: {
          name: name.trim(),
          threshold: meta?.thresholdLabel ? threshold : rule.threshold,
          scope: scope || "",
          webhookUrl: webhookUrl.trim(),
          enabled,
          // See this component's doc comment for the three-state contract.
          signingSecret: signingSecret ? signingSecret : clearSecret ? "" : undefined,
        },
      },
      {
        onSuccess: () => {
          pushToast({ title: `Updated "${ruleName}".`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't update "${ruleName}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    // Handed off to the mutation above — clear it from state immediately (S-13).
    setSigningSecret("");
  }

  return (
    <Modal
      open={rule !== null}
      title={rule ? `Edit "${rule.name}"` : "Edit alert rule"}
      onClose={handleClose}
      initialFocusId={nameId}
    >
      <form onSubmit={handleSubmit} noValidate>
        <label className="form-field" htmlFor={nameId}>
          <span>Name</span>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={200}
          />
        </label>

        <p className="form-field__label">
          Fires when {meta?.label.toLowerCase() ?? "…"}
          <span className="form-field__hint"> — the condition can't be changed after creation.</span>
        </p>

        {meta?.thresholdLabel && (
          <label className="form-field" htmlFor={thresholdId}>
            <span>{meta.thresholdLabel}</span>
            <input
              id={thresholdId}
              type="number"
              min={0}
              value={threshold}
              onChange={(e) => setThreshold(Math.max(0, Number(e.target.value) || 0))}
              required
            />
          </label>
        )}

        <label className="form-field" htmlFor={scopeId}>
          <span>Target</span>
          <select id={scopeId} value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="">Whole fleet</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field" htmlFor={webhookId}>
          <span>Webhook URL (optional)</span>
          <input
            id={webhookId}
            type="url"
            autoComplete="off"
            placeholder="https://…"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
          />
          <span className="form-field__hint">
            {looksLikeHttpsUrl(webhookUrl)
              ? "Leave blank to stop delivering notifications for this rule."
              : "This should be an https:// URL — Comebye will not deliver to a plaintext endpoint."}
          </span>
        </label>

        <label className="form-field" htmlFor={secretId}>
          <span>New webhook signing secret (optional)</span>
          <input
            id={secretId}
            type="password"
            autoComplete="off"
            value={signingSecret}
            disabled={clearSecret}
            onChange={(e) => setSigningSecret(e.target.value)}
          />
          <span className="form-field__hint">
            Sealed on write. Never shown again — leave blank to keep the current secret (if any)
            unchanged.
          </span>
        </label>

        <label className="form-field form-field--checkbox" htmlFor={clearSecretId}>
          <span className="form-field--checkbox__row">
            <input
              id={clearSecretId}
              type="checkbox"
              checked={clearSecret}
              onChange={(e) => {
                setClearSecret(e.target.checked);
                if (e.target.checked) setSigningSecret("");
              }}
            />
            <span>Remove signing secret</span>
          </span>
          <span className="form-field__hint">Future deliveries for this rule go out unsigned.</span>
        </label>

        <label className="form-field form-field--checkbox" htmlFor={enabledId}>
          <span className="form-field--checkbox__row">
            <input
              id={enabledId}
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>Enabled</span>
          </span>
          <span className="form-field__hint">
            A disabled rule is kept but never evaluated — no delivery, no instances.
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending || !name.trim()}
          >
            {mutation.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
